/**
 * Milestone API Service - RTK Query
 * BuilderBel Construction Project Management System
 */

import { baseApi } from './api';
import { ApiResponse } from '../types';
import type {
  CompleteMilestoneRequest,
  CreateMilestoneRequest,
  CreateTemplateRequest,
  CriterionUpdateRequest,
  FromTemplateRequest,
  Milestone,
  MilestoneListParams,
  MilestoneListResponse,
  MilestonePaymentStatus,
  MilestoneStats,
  MilestoneTemplate,
  ProgressUpdateRequest,
  SignoffRequest,
  UpdateMilestoneRequest,
} from '../types/milestone';

/**
 * Milestone API Endpoints
 */
export const milestoneApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // ============================================================================
    // MILESTONE LIST & STATS
    // ============================================================================

    getMilestones: builder.query<
      ApiResponse<MilestoneListResponse>,
      { projectId: string; params?: MilestoneListParams }
    >({
      query: ({ projectId, params }) => ({
        url: `/projects/${projectId}/milestones`,
        params,
      }),
      providesTags: ['Milestone'],
    }),

    getMilestoneStats: builder.query<ApiResponse<MilestoneStats>, { projectId: string }>({
      query: ({ projectId }) => `/projects/${projectId}/milestones/stats`,
      providesTags: ['Milestone'],
    }),

    getMilestoneTimeline: builder.query<ApiResponse<Milestone[]>, { projectId: string }>({
      query: ({ projectId }) => `/projects/${projectId}/milestones/timeline`,
      providesTags: ['Milestone'],
    }),

    getMilestonePaymentStatus: builder.query<
      ApiResponse<MilestonePaymentStatus>,
      { projectId: string }
    >({
      query: ({ projectId }) => `/projects/${projectId}/milestones/payment-status`,
      providesTags: ['Milestone'],
    }),

    // ============================================================================
    // MILESTONE CRUD
    // ============================================================================

    getMilestone: builder.query<ApiResponse<Milestone>, { projectId: string; milestoneId: string }>(
      {
        query: ({ projectId, milestoneId }) => `/projects/${projectId}/milestones/${milestoneId}`,
        providesTags: (_result, _error, { milestoneId }) => [
          { type: 'Milestone', id: milestoneId },
        ],
      }
    ),

    createMilestone: builder.mutation<
      ApiResponse<Milestone>,
      { projectId: string; data: CreateMilestoneRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/milestones`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Milestone'],
    }),

    updateMilestone: builder.mutation<
      ApiResponse<Milestone>,
      { projectId: string; milestoneId: string; data: UpdateMilestoneRequest }
    >({
      query: ({ projectId, milestoneId, data }) => ({
        url: `/projects/${projectId}/milestones/${milestoneId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { milestoneId }) => [
        'Milestone',
        { type: 'Milestone', id: milestoneId },
      ],
    }),

    deleteMilestone: builder.mutation<
      ApiResponse<null>,
      { projectId: string; milestoneId: string }
    >({
      query: ({ projectId, milestoneId }) => ({
        url: `/projects/${projectId}/milestones/${milestoneId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Milestone'],
    }),

    // ============================================================================
    // MILESTONE ACTIONS
    // ============================================================================

    updateMilestoneProgress: builder.mutation<
      ApiResponse<Milestone>,
      { projectId: string; milestoneId: string; data: ProgressUpdateRequest }
    >({
      query: ({ projectId, milestoneId, data }) => ({
        url: `/projects/${projectId}/milestones/${milestoneId}/progress`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { milestoneId }) => [
        'Milestone',
        { type: 'Milestone', id: milestoneId },
      ],
    }),

    completeMilestone: builder.mutation<
      ApiResponse<Milestone>,
      { projectId: string; milestoneId: string; data: CompleteMilestoneRequest }
    >({
      query: ({ projectId, milestoneId, data }) => ({
        url: `/projects/${projectId}/milestones/${milestoneId}/complete`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { milestoneId }) => [
        'Milestone',
        { type: 'Milestone', id: milestoneId },
      ],
    }),

    updateMilestoneCriterion: builder.mutation<
      ApiResponse<Milestone>,
      {
        projectId: string;
        milestoneId: string;
        criterionId: string;
        data: CriterionUpdateRequest;
      }
    >({
      query: ({ projectId, milestoneId, criterionId, data }) => ({
        url: `/projects/${projectId}/milestones/${milestoneId}/criteria/${criterionId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { milestoneId }) => [
        { type: 'Milestone', id: milestoneId },
      ],
    }),

    addMilestoneSignoff: builder.mutation<
      ApiResponse<Milestone>,
      { projectId: string; milestoneId: string; data: SignoffRequest }
    >({
      query: ({ projectId, milestoneId, data }) => ({
        url: `/projects/${projectId}/milestones/${milestoneId}/signoff`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { milestoneId }) => [
        { type: 'Milestone', id: milestoneId },
      ],
    }),

    // ============================================================================
    // TEMPLATES
    // ============================================================================

    getMilestoneTemplates: builder.query<ApiResponse<MilestoneTemplate[]>, { projectId: string }>({
      query: ({ projectId }) => `/projects/${projectId}/milestones/templates`,
      providesTags: ['Milestone'],
    }),

    createMilestoneTemplate: builder.mutation<
      ApiResponse<MilestoneTemplate>,
      { projectId: string; data: CreateTemplateRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/milestones/templates`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Milestone'],
    }),

    createMilestonesFromTemplate: builder.mutation<
      ApiResponse<Milestone[]>,
      { projectId: string; data: FromTemplateRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/milestones/from-template`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Milestone'],
    }),
  }),
});

export const {
  useGetMilestonesQuery,
  useGetMilestoneStatsQuery,
  useGetMilestoneTimelineQuery,
  useGetMilestonePaymentStatusQuery,
  useGetMilestoneQuery,
  useCreateMilestoneMutation,
  useUpdateMilestoneMutation,
  useDeleteMilestoneMutation,
  useUpdateMilestoneProgressMutation,
  useCompleteMilestoneMutation,
  useUpdateMilestoneCriterionMutation,
  useAddMilestoneSignoffMutation,
  useGetMilestoneTemplatesQuery,
  useCreateMilestoneTemplateMutation,
  useCreateMilestonesFromTemplateMutation,
} = milestoneApi;
